import * as React from "react";
import { useEffect, useMemo } from "react";
import { useSelector, useStore } from "react-redux";

import NumberFormat from "react-number-format";
import { Slide } from "@mui/material";

import { InputField } from "../components/InputField.tsx";
import { setSameDayInvestment } from "../Store/roiStore.ts";
import { Total } from "../components/Total.tsx";

import "../styles/pages/sameday.scss";

const INVESTMENT_KEYS = {
    // Legacy monthly key retained so pre-change saved ROIs still read their value.
    monthlySubscriptionAllHospitals: "SameDay Monthly Subscription Fee (All Hospitals)",
    annualSubscriptionAllHospitals: "SameDay Annual Subscription Fee (All Hospitals)",
    implementationAllHospitals: "SameDay Implementation (All Hospitals)",
    perHospitalTrainingFee: "One Time Training & Implementation Fees per Hospital",
    interfaceFees: "One Time Interface Fees (All Interfaces)",
} as const;

export { INVESTMENT_KEYS as SAMEDAY_INVESTMENT_KEYS };

export const SameDayInvestment = () => {
    const sameDay = useSelector((state: any) => state.inputFields.sameDay) ?? {};
    const investment = sameDay.investment ?? {};
    const numberOfHospitals = Number(sameDay.numberOfHospitals ?? 0) || 0;
    const { dispatch } = useStore();

    // New ROIs enter the PAR SameDay fee as an annual amount; legacy ROIs keep their
    // original monthly entry (×12 in the ROI table). The store key differs per
    // cadence so the two never overwrite each other.
    const isLegacyRoi = useSelector((state: any) => !!state.inputFields.isLegacyRoi);
    const cadenceWord = isLegacyRoi ? "Monthly" : "Annual";
    const subscriptionKey = isLegacyRoi
        ? INVESTMENT_KEYS.monthlySubscriptionAllHospitals
        : INVESTMENT_KEYS.annualSubscriptionAllHospitals;

    const update = (name: string, value: any) =>
        dispatch(setSameDayInvestment({ name, value }));

    // Stamp new ROIs as annual-priced so they're not misread as legacy on reload.
    useEffect(() => {
        if (!isLegacyRoi && investment["pricingModel"] !== "annual") {
            update("pricingModel", "annual");
        }
    }, [isLegacyRoi, investment]);

    const implementation = Number(investment[INVESTMENT_KEYS.implementationAllHospitals] ?? 0);
    const perHospitalFee = Number(investment[INVESTMENT_KEYS.perHospitalTrainingFee] ?? 0);
    const interfaceFees = Number(investment[INVESTMENT_KEYS.interfaceFees] ?? 0);

    const totalUpfront = useMemo(
        () => implementation + perHospitalFee * numberOfHospitals + interfaceFees,
        [implementation, perHospitalFee, numberOfHospitals, interfaceFees]
    );

    return (
        <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <div className="sameday-investment-page">
                <h1>PAR Excellence Investment - PAR SameDay</h1>
                <p className="sameday-investment-instructions">
                    Enter the PAR SameDay-specific subscription and one-time fees below.
                    The per-hospital training and implementation fee is multiplied by
                    the number of hospitals from the PAR SameDay Setup step
                    {numberOfHospitals > 0 ? ` (${numberOfHospitals})` : ""}.
                </p>

                <div className="sameday-investment-section">
                    <h3>Subscription</h3>
                    <div className="sameday-investment-grid">
                        <InputField
                            label={`PAR SameDay ${cadenceWord} Subscription Fee (All Hospitals)`}
                            value={investment[subscriptionKey] ?? ""}
                            type="number"
                            prefix="$"
                            onValueInputChange={(_n, value) =>
                                update(subscriptionKey, value)
                            }
                        />
                    </div>
                </div>

                <div className="sameday-investment-section">
                    <h3>One-Time Fees</h3>
                    <div className="sameday-investment-grid">
                        <InputField
                            label="PAR SameDay Implementation (All Hospitals)"
                            value={investment[INVESTMENT_KEYS.implementationAllHospitals] ?? ""}
                            type="number"
                            prefix="$"
                            onValueInputChange={(_n, value) =>
                                update(INVESTMENT_KEYS.implementationAllHospitals, value)
                            }
                        />
                        <InputField
                            label="One Time Training & Implementation Fees per Hospital"
                            value={investment[INVESTMENT_KEYS.perHospitalTrainingFee] ?? ""}
                            type="number"
                            prefix="$"
                            onValueInputChange={(_n, value) =>
                                update(INVESTMENT_KEYS.perHospitalTrainingFee, value)
                            }
                        />
                        <InputField
                            label="One Time Interface Fees (All Interfaces)"
                            value={investment[INVESTMENT_KEYS.interfaceFees] ?? ""}
                            type="number"
                            prefix="$"
                            onValueInputChange={(_n, value) =>
                                update(INVESTMENT_KEYS.interfaceFees, value)
                            }
                        />
                    </div>
                </div>

                <Total
                    total={{
                        title: "Total Upfront Investment",
                        value: (
                            <NumberFormat
                                allowEmptyFormatting
                                value={totalUpfront}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={"$"}
                            />
                        ),
                    }}
                />

                <div className="sameday-investment-note">
                    {isLegacyRoi
                        ? "Annual subscription is calculated as the monthly fee × 12 in the ROI table. "
                        : "The annual subscription fee carries directly into the ROI table (with the standard yearly escalator). "}
                    Per-hospital training/implementation × number of hospitals plus
                    the all-hospital implementation and interface fees roll into Year 1
                    upfront investment.
                </div>
            </div>
        </Slide>
    );
};
